/**
 * Owner: Masa Bayu
 * File: RoutePanel.tsx
 * Date:Time: 2026-09-28 10:30
 * Description: Shell route panels — Account gated · satu pintu Studio.
 */

import type { AuthSessionView } from '../auth/session-types';
import { lazy, Suspense } from 'react';
import { LoginPage } from '../login/LoginPage';
import { AccountRoute } from '../login/AccountRoute';
import {
  DedicatedLibraryScreen,
  DedicatedMyBooksScreen,
  ProfileShell,
  STUDIO_ENTRY,
} from '../author';
import { AuthorRoutes } from './AuthorRoutes';
import { AuthorDiscoveryPage } from '../author/discovery/AuthorDiscoveryPage';
import { BecomeAnAuthorScreen } from '../author/signup/BecomeAnAuthorScreen';
import { ReaderLoadingGate } from '../reader/ReaderLoadingGate';
import { AdminConsole, AdminLoginPage } from '../admin';
import { CommerceCartPanel, CommerceCheckoutPanel, CommerceOrdersPanel } from '../commerce';
import { LibraryLabHost } from '../library/LibraryLabHost';
import { DedicatedCommunityScreen } from '../qxcircle';
import { LiveComingPanel } from '../live';
import { HomeScreen } from '../home';
import {
  DiscoverPanel,
  SearchPanel,
  TaxonomyDetailPanel,
  TaxonomyIndexPanel,
  TopicDetailPanel,
  TopicsExplorerPanel,
} from '../marketplace';
import { createDemoTaxonomyIndex } from '../marketplace/create-demo-taxonomy';
import { MarketplaceDetail } from '../home/marketplace/MarketplaceDetail';
import { MarketplaceHome } from '../home/marketplace/MarketplaceHome';
import { QxCategoryBrowser } from '../home/marketplace/QxCategoryBrowser';
import { isQxMainCategoryId } from '@qx/qxread-core';
import P02LivePage from '../app/p02/page';
import P03LivePage from '../app/p03/page';
import P04LivePage from '../app/p04/page';
import P05LivePage from '../app/p05/page';
import P06LivePage from '../app/p06/page';
import P07LivePage from '../app/p07/page';
import P08LivePage from '../app/p08/page';
import P09LivePage from '../app/p09/page';
import P10LivePage from '../app/p10/page';
import P11LivePage from '../app/p11/page';
import P12LivePage from '../app/p12/page';
import P13LivePage from '../app/p13/page';
import P14LivePage from '../app/p14/page';
import P15LivePage from '../app/p15/page';
import P16LivePage from '../app/p16/page';
import P17LivePage from '../app/p17/page';
import P18LivePage from '../app/p18/page';
import P19LivePage from '../app/p19/page';
import P19DropcapJustifyLivePage from '../app/p19-dropcap/page';
import P19HijrahGlueLivePage from '../app/p19-hijrah/page';
import P19WriterLivePage from '../app/p19-writer/page';
import Fasa23StructuralLivePage from '../app/fasa23/page';
import {
  AdamEntryPanel,
  FlipIntentPanel,
  MediaChromePanel,
  MotionPreferencePanel,
  OfflinePackagePanel,
  SyncQueuePanel,
} from '../advanced';
import { PwaInstallPanel } from '../pwa';
import { QxErrorState } from '../ux';
import { readSearchParam } from './use-shell-route';

/**
 * Owner 2026-10-29 09:22 — Grabfold 100% pada /read. PageCurl / NaturalReader
 * tidak dipasang. Bukan Live deploy.
 */
const GrabfoldSpikeScreen = lazy(() =>
  import('../reader/spike/GrabfoldSpikeScreen').then((m) => ({ default: m.GrabfoldSpikeScreen })),
);

export interface RoutePanelProps {
  readonly path: string;
  readonly search?: string;
  readonly allowed: boolean;
  readonly chromeMode: string;
  readonly sessionReady?: boolean;
  readonly signedIn?: boolean;
  readonly signedInName?: string | null;
  readonly authSession?: AuthSessionView | null;
  readonly onNavigate: (path: string) => void;
  readonly onSignedIn?: (session: AuthSessionView) => void;
  readonly onSignOut?: () => void;
}

function matchTaxonomyPath(
  path: string,
  prefix: '/categories' | '/collections' | '/topics',
): string | null {
  if (!path.startsWith(`${prefix}/`)) {
    return null;
  }
  const slug = path.slice(prefix.length + 1);
  return slug.length > 0 && !slug.includes('/') ? slug : null;
}

export function RoutePanel({
  path,
  search = '',
  allowed,
  chromeMode,
  sessionReady = false,
  signedIn = false,
  signedInName = null,
  authSession = null,
  onNavigate,
  onSignedIn = () => {},
  onSignOut = () => {},
}: RoutePanelProps) {
  if (!allowed) {
    return (
      <section className="route-panel" aria-labelledby="route-title">
        <h2 id="route-title">Route closed</h2>
        <QxErrorState
          title="This route is not available"
          description={`${path} is outside the open shell. Return to Home or Library.`}
          onRetry={() => {
            onNavigate('/');
          }}
        />
      </section>
    );
  }

  if (path === '/') {
    return <HomeScreen chromeMode={chromeMode} onNavigate={onNavigate} />;
  }

  // §452 — Become an author: daftar akaun penerbit di qxp-hub, bukan dashboard kosong.
  if (path === '/become-an-author') {
    return <BecomeAnAuthorScreen onNavigate={onNavigate} />;
  }

  // §376 — senarai penuras pindah dari home ke laluan khusus (All Books /
  // Bestsellers); penuras awal datang daripada query string.
  if (path === '/all-books') {
    return <MarketplaceHome onNavigate={onNavigate} search={search} />;
  }

  if (path === '/bestsellers') {
    return <MarketplaceHome onNavigate={onNavigate} search="sort=newest" />;
  }

  if (path === '/p02') {
    return <P02LivePage />;
  }

  if (path === '/p03') {
    return <P03LivePage />;
  }

  if (path === '/p04') {
    return <P04LivePage />;
  }

  if (path === '/p05') {
    return <P05LivePage />;
  }

  if (path === '/p06') {
    return <P06LivePage />;
  }

  if (path === '/p07') {
    return <P07LivePage />;
  }

  if (path === '/p08') {
    return <P08LivePage />;
  }

  if (path === '/p09') {
    return <P09LivePage />;
  }
  if (path === '/p10') {
    return <P10LivePage />;
  }
  if (path === '/p11') {
    return <P11LivePage />;
  }
  if (path === '/p12') {
    return <P12LivePage />;
  }
  if (path === '/p13') {
    return <P13LivePage />;
  }
  if (path === '/p14') {
    return <P14LivePage />;
  }
  if (path === '/p15') {
    return <P15LivePage />;
  }
  if (path === '/p16') {
    return <P16LivePage />;
  }

  if (path === '/p17') {
    return <P17LivePage />;
  }

  if (path === '/p18') {
    return <P18LivePage />;
  }

  if (path === '/p19') {
    return <P19LivePage />;
  }

  if (path === '/p19-dropcap') {
    return <P19DropcapJustifyLivePage />;
  }

  if (path === '/p19-hijrah') {
    return <P19HijrahGlueLivePage />;
  }

  if (path === '/p19-writer') {
    return <P19WriterLivePage />;
  }

  if (path === '/p23-structural') {
    return <Fasa23StructuralLivePage />;
  }

  if (path === '/browse' || path === '/discover') {
    return <DiscoverPanel onNavigate={onNavigate} />;
  }

  if (path === '/search') {
    return <SearchPanel onNavigate={onNavigate} search={search} />;
  }

  if (path === '/cart') {
    return <CommerceCartPanel onNavigate={onNavigate} />;
  }

  if (path === '/checkout') {
    return <CommerceCheckoutPanel onNavigate={onNavigate} />;
  }

  if (path === '/orders' || path.startsWith('/orders/')) {
    return <CommerceOrdersPanel onNavigate={onNavigate} />;
  }

  const taxonomy = createDemoTaxonomyIndex();

  if (path === '/topics') {
    return <TopicsExplorerPanel onNavigate={onNavigate} />;
  }

  const topicId = matchTaxonomyPath(path, '/topics');
  if (topicId !== null) {
    return <TopicDetailPanel topicId={decodeURIComponent(topicId)} onNavigate={onNavigate} />;
  }

  if (path === '/categories') {
    return <QxCategoryBrowser onNavigate={onNavigate} />;
  }

  /** §405 — /categories/<id> = pelayar QX; id sah membuka kategori fokus. */
  const categoryFocus = matchTaxonomyPath(path, '/categories');
  if (categoryFocus !== null) {
    const focusId = decodeURIComponent(categoryFocus);
    return (
      <QxCategoryBrowser
        onNavigate={onNavigate}
        focusCategory={isQxMainCategoryId(focusId) ? focusId : undefined}
      />
    );
  }

  if (path === '/collections') {
    return (
      <TaxonomyIndexPanel kind="collection" nodes={taxonomy.collections} onNavigate={onNavigate} />
    );
  }

  const collectionSlug = matchTaxonomyPath(path, '/collections');
  if (collectionSlug !== null) {
    return <TaxonomyDetailPanel kind="collection" slug={collectionSlug} onNavigate={onNavigate} />;
  }

  if (path === '/library') {
    return (
      <ProfileShell activeId="library" onNavigate={onNavigate}>
        <DedicatedLibraryScreen onNavigate={onNavigate} />
        {import.meta.env.DEV ? <LibraryLabHost /> : null}
      </ProfileShell>
    );
  }
  if (path.startsWith('/library/open/') || path.startsWith('/library/resume/')) {
    return (
      <ProfileShell activeId="library" onNavigate={onNavigate}>
        <DedicatedLibraryScreen onNavigate={onNavigate} deepLinkPath={path} />
        {import.meta.env.DEV ? <LibraryLabHost /> : null}
      </ProfileShell>
    );
  }

  if (path === '/my-books') {
    return (
      <ProfileShell activeId="books" onNavigate={onNavigate}>
        <DedicatedMyBooksScreen onNavigate={onNavigate} />
      </ProfileShell>
    );
  }

  if (path === '/qxeditor' || path.startsWith('/qxeditor/')) {
    return <AuthorRoutes path={STUDIO_ENTRY} onNavigate={onNavigate} />;
  }

  const readMatch = path.match(/^\/read\/([^/]+)$/);
  if (readMatch !== null) {
    const publicationId = decodeURIComponent(readMatch[1] ?? '');
    const sampleRequested = readSearchParam(search ?? '', 'sample') === '1';
    return (
      <Suspense
        fallback={
          <ReaderLoadingGate
            engine="grabfold"
            reason={sampleRequested ? 'Membuka halaman sampel…' : 'Membuka buku anda…'}
          />
        }
      >
        <GrabfoldSpikeScreen
          publicationId={publicationId}
          onNavigate={onNavigate}
          sample={sampleRequested}
          search={search ?? ''}
        />
      </Suspense>
    );
  }

  if (path === '/circle' || path.startsWith('/circle/')) {
    return (
      <ProfileShell activeId="community" onNavigate={onNavigate}>
        <DedicatedCommunityScreen path={path} onNavigate={onNavigate} />
      </ProfileShell>
    );
  }

  if (path === '/live' || path.startsWith('/live/')) {
    return <LiveComingPanel onNavigate={onNavigate} />;
  }

  if (path === '/authors') {
    // §411 — Author Discovery (replika mockup Owner; carian + penapis dalam page).
    return <AuthorDiscoveryPage onNavigate={onNavigate} search={search} />;
  }

  if (path === '/author' || path.startsWith('/author/')) {
    return <AuthorRoutes path={path} onNavigate={onNavigate} />;
  }

  if (path === '/admin/login') {
    // §468-E — log masuk pentadbir berdiri sendiri: tiada Google, tiada pemilih
    // Reader/Publisher, tiada pendaftaran.
    return (
      <AdminLoginPage
        signedInName={signedInName}
        onNavigate={onNavigate}
        onSignedIn={onSignedIn}
        onSignOut={onSignOut}
      />
    );
  }

  if (path === '/admin' || path.startsWith('/admin/')) {
    // §467-D — satu konsol pentadbir (5 seksyen) yang membaca hub. Seksyen demo
    // lama (executive/moderation/commerce/audit/security) tidak dipaparkan lagi.
    return <AdminConsole path={path} onNavigate={onNavigate} />;
  }

  if (path === '/account' || path.startsWith('/account/')) {
    return (
      <AccountRoute
        path={path}
        sessionReady={sessionReady}
        signedIn={signedIn}
        signedInName={signedInName}
        authSession={authSession}
        onNavigate={onNavigate}
        onSignedIn={onSignedIn}
        onSignOut={onSignOut}
      />
    );
  }

  if (path === '/login') {
    return (
      <LoginPage
        search={search}
        signedInName={signedInName}
        onNavigate={onNavigate}
        onSignedIn={onSignedIn}
        onSignOut={onSignOut}
      />
    );
  }

  if (path === '/offline') {
    return <PwaInstallPanel onNavigate={onNavigate} />;
  }

  if (path === '/packages') {
    return <OfflinePackagePanel onNavigate={onNavigate} />;
  }

  if (path === '/sync') {
    return <SyncQueuePanel onNavigate={onNavigate} />;
  }

  if (path === '/media') {
    return <MediaChromePanel onNavigate={onNavigate} />;
  }

  if (path === '/flip') {
    return <FlipIntentPanel onNavigate={onNavigate} />;
  }

  if (path === '/motion') {
    return <MotionPreferencePanel onNavigate={onNavigate} />;
  }

  if (path === '/adam') {
    return <AdamEntryPanel onNavigate={onNavigate} />;
  }

  if (path.startsWith('/publication/')) {
    return (
      <MarketplaceDetail
        publicationId={decodeURIComponent(path.slice('/publication/'.length))}
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <section className="route-panel" aria-labelledby="route-title">
      <h2 id="route-title">Unknown shell path</h2>
      <p>
        <code>{path}</code>
      </p>
    </section>
  );
}
